import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import useInfiniteScroll from '../../../Components/ListInfiniteScroll/index.controller';
import { ReservationType } from '../../../Components/Sheets/BookReservation/types';
import constants from '../../../Config/constants';
import usePageView from '../../../Hooks/usePageView';
import useReservationBooking from '../../../Hooks/useReservationBooking';
import {
  ReservationNavigatorProps,
  RootRouteProps,
} from '../../../Routes/list';
import { useGetListingQuery } from '../../../RTK/api/reservations';
import { Listing } from '../../../RTK/api/reservations/types';
import { selectReservationFilter } from '../../../RTK/filter/selectors';
import { DBDATE, DBTIME } from '../../../types/date';

import useListingHandler from './useListingHandler';

dayjs.extend(customParseFormat);

const useListingViewModel = () => {
  const navigation =
    useNavigation<
      StackNavigationProp<RootRouteProps & ReservationNavigatorProps>
    >();
  const {
    availabilityRef,
    selectBookingSchedule,
    navigateToCheckoutDestination,
  } = useReservationBooking();
  const listingFilter = useSelector(selectReservationFilter);

  const { selectedFilter, requestPayload, requestSkip } = useListingHandler();

  const isEvent =
    listingFilter.reservationType?.value === constants.RESERVATION_TYPE.EVENT;

  usePageView({
    screen_class: 'Reservation',
    screenName: 'Reservation | Home',
  });

  const {
    data: listingData,
    isLoading: isListingDataLoading,
    isLoadingMore: isListingLoadingMore,
    refreshing: isListingRefreshing,
    onMomentumScrollBegin,
    onReachedEnd,
    onRefresh,
  } = useInfiniteScroll<Listing>({
    loaderCount: 3,
    queryParams: requestPayload,
    queryOptions: { skip: requestSkip },
    useQuery: useGetListingQuery,
  });

  const lastSectionText = useMemo(() => {
    if (isEvent) {
      if (
        listingFilter?.dateText === constants.eventFilterData[0].label ||
        !listingFilter?.dateText
      ) {
        return 'All upcoming';
      } else {
        return `Upcoming ${listingFilter.dateText.toLowerCase()}`;
      }
    } else {
      return 'All Reservation';
    }
  }, [listingFilter?.dateText, isEvent]);

  const _onBookNow = useCallback(
    (store, data, timeSlot: string) => {
      const selectedTsDBTimeFormat = dayjs(
        timeSlot,
        constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
      ).format(constants.DBTIME_FORMAT) as DBTIME;
      selectBookingSchedule({
        itemInfo: {
          ...data,
          experienceSelectedTs: selectedTsDBTimeFormat,
          type: ReservationType.DINING,
        },
        dateAndTime: {
          date: selectedFilter.date,
          time: selectedTsDBTimeFormat,
        },
        size: selectedFilter.party_size,
        storeInfo: store,
        onSuccess: (result) =>
          navigateToCheckoutDestination(navigation, result),
      });
    },
    [
      navigation,
      selectedFilter.date,
      selectedFilter.party_size,
      navigateToCheckoutDestination,
      selectBookingSchedule,
    ],
  );

  return {
    availabilityRef,
    isListingDataLoading,
    isListingLoadingMore,
    isListingRefreshing,
    lastSectionText,
    listingData,
    listingFilter,
    selectedFilter,
    onBookNow: _onBookNow,
    onMomentumScrollBegin,
    onReachedEnd,
    onRefresh,
  };
};

export default useListingViewModel;
